"use client";

import { JSX, useEffect, useRef } from "react";
import { beaconRouteChange } from "rivals/components/shared/AnalyticsGa/utils";
import useGetCurrentUser from "rivals/services/getCurrentUser/useGetCurrentUser";
import { Props } from "./types";

export default function UrlUpdater({
  children,
  index,
  isInfiniteScrollEnabled,
  site,
  slug,
  topicTags
}: Props): JSX.Element {
  const observerTarget = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useGetCurrentUser();

  const userType = data?.user?.googleAnalyticsUserType ?? "anonymous";

  useEffect(() => {
    if (isInfiniteScrollEnabled && index > 0 && !isLoading) {
      const observer = new IntersectionObserver(
        entries => {
          const target = entries[0];
          if (target.isIntersecting) {
            // Per https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#using-the-native-history-api
            window.history.pushState(null, "", slug);
            if (
              window.rapidInstance &&
              site.contentSpaceId &&
              topicTags !== undefined
            ) {
              // Inform Rapid of the route change
              beaconRouteChange(
                site.contentSpaceId,
                process.env.NEXT_PUBLIC_RAPID_GA_TAG_ID || "",
                userType,
                topicTags
              );
            }
          }
        },
        {
          // These margins prevent the intersection to trigger as soon as the edges of the content become visible
          // while preventing observer twitches derived from multiple articles being visible, tread with caution
          rootMargin: "-50% 0px -50% 0px"
        }
      );

      const element = observerTarget.current;

      if (element) {
        observer.observe(element);
      }

      return () => {
        if (element) {
          observer.unobserve(element);
        }
      };
    }
  }, [
    index,
    isInfiniteScrollEnabled,
    isLoading,
    observerTarget,
    site.contentSpaceId,
    slug,
    topicTags,
    userType
  ]);

  return <div ref={observerTarget}>{children}</div>;
}
